import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useOnline } from '@vueuse/core';
import { useAccount } from './useAccount';
import { useStore } from './store';
import { emitter } from 'shared/helpers/mitt';
import mutationTypes from 'dashboard/store/mutation-types';

export const SUBSCRIPTION_SYNC_EVENT = 'account.subscription.updated';
export const SUBSCRIPTION_ACCESS_DENIED_EVENT = 'account.subscription.denied';
export const SUBSCRIPTION_CONFIGURATION_EVENT =
  'subscription.configuration.updated';
export const SUBSCRIPTION_CONFIGURATION_KEY =
  'chatmuser:subscription-configuration';

export const isSubscriptionAccessError = error =>
  error?.response?.status === 402 &&
  error.response.data?.code === 'subscription_access_required';

export function notifySubscriptionConfiguration() {
  window.dispatchEvent(new Event(SUBSCRIPTION_CONFIGURATION_EVENT));
  try {
    window.localStorage.setItem(
      SUBSCRIPTION_CONFIGURATION_KEY,
      String(Date.now())
    );
    return true;
  } catch {
    return false;
  }
}

export function useSubscriptionSync() {
  const { accountId, currentAccount, route } = useAccount();
  const store = useStore();
  const online = useOnline();
  const subscriptionAccess = computed(
    () => currentAccount.value?.subscription_access
  );
  const blocked = computed(
    () =>
      ['pending', 'suspended'].includes(subscriptionAccess.value?.state) ||
      subscriptionAccess.value?.user_allowed === false
  );
  const featureUnavailable = computed(
    () =>
      !!subscriptionAccess.value &&
      !!route.meta?.featureFlag &&
      !currentAccount.value?.features?.[route.meta.featureFlag]
  );
  const inboxUnavailable = computed(() => {
    const inboxId = Number(route.params?.inbox_id);
    const allowedIds = subscriptionAccess.value?.allowed_inbox_ids;
    return (
      !!inboxId &&
      Array.isArray(allowedIds) &&
      !allowedIds.some(id => Number(id) === inboxId)
    );
  });
  const showSubscriptionGate = computed(
    () =>
      (blocked.value || featureUnavailable.value || inboxUnavailable.value) &&
      !(
        subscriptionAccess.value?.can_manage_billing &&
        ['payments_index', 'billing_settings_index'].includes(route.name)
      )
  );
  let interval;
  let deadline;
  let mounted = false;
  let generation = 0;
  let pendingGeneration = null;
  let controller;

  const inboxScope = computed(() => {
    const allowedIds = subscriptionAccess.value?.allowed_inbox_ids;
    if (
      allowedIds === undefined ||
      Number(currentAccount.value?.id) !== accountId.value
    )
      return undefined;
    return JSON.stringify({
      accountId: accountId.value,
      allowedIds: blocked.value ? [] : allowedIds,
    });
  });
  watch(
    inboxScope,
    value => {
      if (value === undefined) return;
      store.commit(
        mutationTypes.SET_CONVERSATION_INBOX_SCOPE,
        JSON.parse(value).allowedIds
      );
    },
    { immediate: true, flush: 'sync' }
  );

  async function refreshSubscriptionAccess() {
    if (
      !mounted ||
      !online.value ||
      document.visibilityState !== 'visible' ||
      !accountId.value
    )
      return;
    const currentGeneration = generation;
    if (pendingGeneration === currentGeneration) return;
    pendingGeneration = currentGeneration;
    controller = new AbortController();
    try {
      await store.dispatch('accounts/refresh', {
        accountId: accountId.value,
        signal: controller.signal,
      });
    } finally {
      if (generation === currentGeneration) pendingGeneration = null;
    }
  }

  function scheduleDeadline() {
    clearTimeout(deadline);
    const access = subscriptionAccess.value;
    if (!mounted || access?.state !== 'grace') return;
    const delay =
      new Date(access.grace_ends_at) - new Date(access.evaluated_at);
    if (!Number.isFinite(delay)) return;
    deadline = setTimeout(
      refreshSubscriptionAccess,
      Math.min(Math.max(250, delay + 250), 2147483647)
    );
  }

  function onStorage(event) {
    if (event.key === SUBSCRIPTION_CONFIGURATION_KEY)
      refreshSubscriptionAccess();
  }

  function onAccessDenied({ accountId: deniedId, access }) {
    if (!mounted || Number(deniedId) !== accountId.value) return;
    if (['pending', 'suspended'].includes(access?.state)) {
      if (subscriptionAccess.value?.state === access.state) return;
      controller?.abort();
      generation += 1;
      store.commit('accounts/EDIT_ACCOUNT', {
        id: accountId.value,
        subscription_access: {
          ...subscriptionAccess.value,
          state: access.state,
        },
      });
    }
    refreshSubscriptionAccess();
  }

  watch(accountId, () => {
    controller?.abort();
    generation += 1;
    if (
      subscriptionAccess.value?.allowed_inbox_ids === undefined ||
      Number(currentAccount.value?.id) !== accountId.value
    )
      store.commit(mutationTypes.SET_CONVERSATION_INBOX_SCOPE, null);
    clearTimeout(deadline);
    refreshSubscriptionAccess();
  });
  watch(online, connected => {
    if (connected) refreshSubscriptionAccess();
  });
  watch(subscriptionAccess, scheduleDeadline, { deep: true });
  const signature = computed(() =>
    JSON.stringify({
      id: currentAccount.value?.id,
      features: Object.entries(currentAccount.value?.features || {}).sort(
        ([left], [right]) => left.localeCompare(right)
      ),
      limits: currentAccount.value?.usage_limits,
      state: subscriptionAccess.value?.state,
      user_allowed: subscriptionAccess.value?.user_allowed,
      grace_ends_at: subscriptionAccess.value?.grace_ends_at,
      allowed_inbox_ids: subscriptionAccess.value?.allowed_inbox_ids,
    })
  );
  watch(signature, () => {
    if (Number(currentAccount.value?.id) === accountId.value)
      emitter.emit(SUBSCRIPTION_SYNC_EVENT, { accountId: accountId.value });
  });

  onMounted(() => {
    mounted = true;
    emitter.on(SUBSCRIPTION_ACCESS_DENIED_EVENT, onAccessDenied);
    interval = setInterval(refreshSubscriptionAccess, 30000);
    document.addEventListener('visibilitychange', refreshSubscriptionAccess);
    window.addEventListener('focus', refreshSubscriptionAccess);
    window.addEventListener('storage', onStorage);
    window.addEventListener(
      SUBSCRIPTION_CONFIGURATION_EVENT,
      refreshSubscriptionAccess
    );
    scheduleDeadline();
    refreshSubscriptionAccess();
  });
  onUnmounted(() => {
    controller?.abort();
    mounted = false;
    emitter.off(SUBSCRIPTION_ACCESS_DENIED_EVENT, onAccessDenied);
    generation += 1;
    clearInterval(interval);
    clearTimeout(deadline);
    document.removeEventListener('visibilitychange', refreshSubscriptionAccess);
    window.removeEventListener('focus', refreshSubscriptionAccess);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(
      SUBSCRIPTION_CONFIGURATION_EVENT,
      refreshSubscriptionAccess
    );
  });

  return {
    subscriptionAccess,
    showSubscriptionGate,
    featureUnavailable,
    inboxUnavailable,
    refreshSubscriptionAccess,
    subscriptionRevision: signature,
  };
}
